// Define interfaces for dream data
export interface DreamCharacter {
  id: string;
  character: string;
  title: string;
  description: string;
}

export interface DreamObject {
  id: string;
  name: string;
  description: string;
  longDescription?: string;
  relatedCharacters: string[];
  isPopular?: boolean; // New field for popular dreams
}

// Import data from the JSON file (using dynamic import to avoid issues)
import dreamDataJson from './dreamData.json';

// Parse the JSON data - characters have ids that start with 'char-'
export const characterData: DreamCharacter[] = dreamDataJson
  .filter(item => item.id && typeof item.id === 'string' && item.id.startsWith('char-'))
  .map(item => item as DreamCharacter);

// Get all the dream objects - objects have ids that start with 'obj-'
export const dreamObjects: DreamObject[] = dreamDataJson
  .filter(item => item.id && typeof item.id === 'string' && item.id.startsWith('obj-'))
  .map(item => ({
    ...item,
    isPopular: ['გველი', 'სისხლი', 'ჯვარი', 'სასაფლაო', 'ოქრო', 'ცხენი', 'ეკლესია'].includes(item.name)
  } as DreamObject));

// Export all data for direct access if needed
export const allDreamData = dreamDataJson;

// For compatibility with existing code
export const objectData = dreamObjects;

// Use the enhanced formatting function from dreamDataLazy for consistency
export { formatObjectNameForUrl, normalizeObjectName } from './dreamDataLazy';

export const isDoubleEncoded = (str: string): boolean => {
  try {
    const decoded = decodeURIComponent(str);
    const reEncoded = encodeURIComponent(decoded);
    return str !== reEncoded && str.includes('%25');
  } catch {
    return false;
  }
};

export const fixDoubleEncoding = (str: string): string => {
  try {
    // If it's double-encoded, decode twice
    if (isDoubleEncoded(str)) {
      return decodeURIComponent(decodeURIComponent(str));
    }
    return decodeURIComponent(str);
  } catch {
    return str;
  }
};

// Updated to use the async version from dreamDataLazy
export const getObjectByName = async (name: string): Promise<DreamObject | undefined> => {
  const { getObjectByName: asyncGetObjectByName } = await import('./dreamDataLazy');
  return asyncGetObjectByName(name);
};

export const getCharacterByValue = (character: string): DreamCharacter | undefined => {
  const decodedCharacter = decodeURIComponent(character);
  
  const exactMatch = characterData.find(char => 
    char.character?.toLowerCase() === decodedCharacter?.toLowerCase()
  );
  
  if (exactMatch) return exactMatch;
  
  return characterData.find(char => 
    char.character?.toLowerCase().includes(decodedCharacter?.toLowerCase()) ||
    decodedCharacter?.toLowerCase().includes(char.character?.toLowerCase())
  );
};

export const getObjectsByCharacter = (character: string): DreamObject[] => {
  if (!character) return [];
  
  const decodedCharacter = decodeURIComponent(character);
  
  return dreamObjects.filter(obj => {
    const firstChar = obj.name.charAt(0);
    return firstChar.toLowerCase() === decodedCharacter.toLowerCase();
  });
};

export const searchObjects = (query: string): DreamObject[] => {
  if (!query || query.trim().length === 0) return [];
  
  const lowercaseQuery = query.toLowerCase();
  return dreamObjects.filter(obj => {
    return (
      obj.name.toLowerCase().includes(lowercaseQuery) || 
      obj.description.toLowerCase().includes(lowercaseQuery)
    );
  });
};

export const getPopularDreams = (): DreamObject[] => {
  return dreamObjects.filter(obj => obj.isPopular === true);
};
